import { EditType } from 'src/types/edit';
import { InsightSummaryPage } from 'src/types/pages/insights';

export const INTRA_ORGANIZATIONAL: InsightSummaryPage = {
  page_id: 'insights-32',
  section_order: ['header', 'summary'],
  header: {
    visible: true,
    content: {
      title: 'Intra-Organizational',
      body: 'Mediators observe that if FMCS would seize the changing landscape in order to innovate, streamline, de-silo, and create stronger teams, then the result would be better services, and a stronger professional network.',
      header_type: 'full',
    },
  },
  summary: {
    visible: true,
    content: {
      highlight_link_style: {
        default_color: '#11304B',
        active_color: '#48bf53',
      },
      paragraphs: [
        {
          label: 'Overview & Recommendations ',
          text: 'Virtually all participants in the facilitated conversations expressed they enjoyed hearing different perspectives from around the country. They further stated it felt good to break out of personal and geographic silos to interact with groups of FMCS people – many of whom they never met– as colleagues and individuals. The participants also said opportunities to engage with each other in the setting provided by the pilot program feels <Embed getEmbedInfo={getEmbedInfo}>collegial and inclusive-5248976</Embed>. Several people shared teaming stories on professional projects.  This type of exercise helps them be part of a “Team” and fosters a new,  <Embed getEmbedInfo={getEmbedInfo}>uplifting ‘esprit de corps’-5250371</Embed> within FMCS<br/><br/>Translating insights into <Embed getEmbedInfo={getEmbedInfo}>areas of action-5249055</Embed>, the following recommendations are offered to support leadership decision-making and planning:<br/><ul><li> Agency leadership is transparent, and practices clear, consistent, honest, and thorough communication nationwide.</li><li>Agency leadership promotes <Embed getEmbedInfo={getEmbedInfo}>intra-agency conversations-5249325</Embed> and celebrations by reasonably setting aside time and budget (regionally & nationally) for developing and consistently conducting events which bring the members of the agency together virtually or in-person.</li><li>Agency leadership supports nationwide collaborations for administrative and field workers in servicing internal/external clients and <Embed getEmbedInfo={getEmbedInfo}>cross-departmental-5249460</Embed> project work.</li></ul>',
        },
        {
          label: 'Engagement',
          text: "Engagement is more than merely paying attention to something, and mediators are perceptive to the various ways to recognize and nurture this emotional state. Participants in our conversations observe that the field of conflict management requires practitioners to place a high degree of commitment to not just their profession, but to their agency. <br/><br/>Speakers like <Embed getEmbedInfo={getEmbedInfo}>Speaker DA-5249085</Embed> have high hopes that FMCS will evolve into an agency viewed as a “major resource for helping resolve conflict in our society” due to the high rate of intense political polarization. <Embed getEmbedInfo={getEmbedInfo}>Speaker MB-5250458</Embed> who arrived at FMCS after a significant career in another federal context and shares that FMCS is uniquely situated both internally and externally to promote engagement because of its one-stop-shop services and professional resources: “I do see FMCS as a growing agency...I mean, you got the best of all worlds right here. You got mediators, you got conciliators, you got everything here. I think that's what makes FMCS unique because you have everything right there.” <br/><br/>How does having many mediators and conflict management specialists under one umbrella provide high engagement? Speaker SB and Speaker JB observe that it is teamwork that has worked in the past to transform stated interest into committed buy-in, and therefore should be FMCS's strengthening of teams in the future that will continue such engagement. <br/><br/>To deliver what <Embed getEmbedInfo={getEmbedInfo}>Speaker JB-5249324</Embed> calls high quality service with focus and intention, they find that “the teaming approach is proving to be so powerful. There's something about these platforms where unless it's just a garden variety small group, perhaps collective bargaining scenario... almost any delivery, service can be improved, I think, by adding people to your team.” <Embed getEmbedInfo={getEmbedInfo}>Speaker SA-5250366</Embed> supports this sentiment and concurs that mediator teams are not only effective but are also sources of positive regard: “I think what I would like to see would be teams of people working on the projects that give them the most energy and joy in their work.” <br/><br/><Embed getEmbedInfo={getEmbedInfo}>Speaker SB-5249092</Embed> shared their belief on what factors best facilitate the agency's continued engagement and their role in this growth; “For me, my hope and dreams are for FMCS to continue to be accepted into CBMs and any other service that customers want, and to see us not get too much smaller, and to just grow and adapt to changes, and I see my role in that the same as I am now, an administrative assistant. And whether or not we're in offices or working from home, I'd like to still see us grow.”",
        },
        {
          label: 'Collaboration',
          text: "For mediators, a strong foundation of cooperation and collaboration is what brings their teams together for the common purpose of solving goals. Participants express their belief in a long view for success which involves  FMCS focusing on technological innovation by way of effective cooperation. <Embed getEmbedInfo={getEmbedInfo}>Speaker KA-5250350</Embed> makes the case for how concerted group effort deployed toward the shared goals of gearing up mediators up to virtual platforms, was the secret to FMCS success; “Agency wise, I think the big benefit for FMCS was collaboration, certainly training and knowledge in the technology, and teaming.” Another case for teams was what <Embed getEmbedInfo={getEmbedInfo}>Speaker DB-5249066</Embed> observed regarding balancing a mediator's existing, pre-pandemic networks with a willingness to embrace new, remote teams. Despite the disruption of pandemic lockdowns, they observe that; “the team that I was from still stayed together. We still talk. We still do retirement stuff. We still have support groups. We still meet, just like we did just virtually, sometimes in person. And then I got to meet a new team and make new friends and new colleagues down in the southeast. So that was the positive of that. So now I feel like I'm on two teams with two different sets of people and I feel like I get to know more folks and I think that's nice.” <br/><br/>This cross-collaboration proved to be a valuable model for new mediators such as <Embed getEmbedInfo={getEmbedInfo}>Speaker BD-5249103</Embed> who certainly felt at a loss because they began their service with FMCS shortly before pandemic lockdowns. They shared that despite being a new mediator, they were valued and recognized for their unique contribution as a “digital native” to the established team. “I came on to the agency in November of 2019, so I had about four months of in-person work before we went fully remote…I consider myself a digital native, and so I really did start feeling as though I was a value add when it comes to application of technology to the work of the agency.”  Speaker BD's experience demonstrates the power of pooling skill sets and collaboration to break down hierarchies and build up a professional community.",
        },
        {
          label: 'Awareness',
          text: "Even as mediators shared the many ways that the agency has performed well and shown resilience during trying times, they also pointed to shortfalls where FMCS can make improvements in service, policy, and practice. <br/><br/><Embed getEmbedInfo={getEmbedInfo}>Speaker DB-5249081</Embed> would like to see FMCS become more active in soliciting feedback from their employees rather than focus on customer service; “I hope to have more internal meetings, not just with customers, at FMCS in the future to see our colleagues, at least regionally starting there, whatever it is that we can do.” And because of the agency’s current size, they are positioned for growth: “Other networks and departments are so huge, with thousands and thousands of people, where we don't. And we should be able to capitalize on that through our ability to meet together as much as possible, and as practical and as affordable.” <Embed getEmbedInfo={getEmbedInfo}>Speaker JB-5250611</Embed> echoes this idea because they believe it signals to mediators that their professional practice is valued: “I want them to do some type of appreciative inquiry on us. I want them to find where all of these best practices are and use it to move the organization in their direction.” Speaker JB likens those who are inflexible and unwilling to make changes to “an anchor on the sea bed” and they should be left alone so others may forge ahead to influence organizational changes.<br/><br/>Another strong sentiment shared by mediators is that FMCS should trust in their mediators to act on their professional skills and make the choices needed to deliver excellent services for clients. <Embed getEmbedInfo={getEmbedInfo}>Speaker BE-5250614</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker TC-5250891</Embed> agree on the view that Speaker TC’s states that “all the staff in FMCS are hired because they're professionals and they know their work.” And as such, their judgment should be supported, particularly on whether meetings should be held virtually or in-person.",
        },
      ],
    },
  },
  edit_metadata: {
    page_label: 'Intra-Organizational',
    page_id: 'insights-32',
    subroute: true,
    sections: [
      {
        section_label: 'Introduction',
        edit_blocks: [
          {
            block_label: 'Header',
            match: 'header.content',
            type: EditType.banner,
          },
        ],
      },
      {
        section_label: 'Highlight Link Colors',
        edit_blocks: [
          {
            block_label: 'Color',
            match: 'summary.content.highlight_link_style.default_color',
            type: EditType.color,
          },
          {
            block_label: 'Active Color',
            match: 'summary.content.highlight_link_style.active_color',
            type: EditType.color,
          },
        ],
      },
      {
        section_label: 'Paragraphs',
        update_section: {
          match: 'summary.content.paragraphs',
          label: 'Add Paragraph',
          empty: {
            label: 'Default Label',
            text: 'Default Text',
          },
          newSectionMetadata: {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: `summary.content.paragraphs[index].label`,
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: `summary.content.paragraphs[index].text`,
                type: EditType.embed_string,
              },
            ],
          },
        },
        sub_sections: [
          {
            section_label: 'Overview',
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[0].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[0].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[1].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[1].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[2].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[2].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[3].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[3].text',
                type: EditType.embed_string,
              },
            ],
          },
        ],
      },
    ],
  },
};
